import React from 'react';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemButton,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Stack,
  Tooltip,
  Box
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useCatalogContext } from './CatalogContext';

const CatalogProductListItem = ({ produto }) => {
  const {
    selectedProducts,
    handleProductSelection,
    formatPrice,
    editingPriceProductId,
    editingPriceSkuId,
    newPrice,
    setNewPrice,
    handleSavePrice,
    handleCancelEditPrice,
    handleStartEditPrice,
    handleToggleProductActive
  } = useCatalogContext();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const isSelected = selectedProducts.includes(produto.id || produto._id);
  const isEditing = editingPriceProductId === produto.id && editingPriceSkuId === produto?.skus[0]?.partnerId;
  const hasImage = produto?.images?.length > 0 && produto.images[0];
  const isActive = produto.isProductActive;
  const stock = produto?.skus?.[0]?.amount || 0;

  // Preço de Mercado e Vitrine
  const precoMercado = produto?.skus?.[0]?.price || 0;
  const precoVitrine = produto?.meli_props?.price || null;
  const meliStatus = produto?.meli_props?.status || '-';
  const meliAvailable = produto?.meli_props?.available_quantity ?? '-';
  const meliPermalink = produto?.meli_props?.permalink || null;
  const meliDateCreated = produto?.meli_props?.date_created ? new Date(produto.meli_props.date_created).toLocaleDateString() : '-';
  const meliSku = produto?.meli_props?.seller_custom_field || '-';
  const canal = produto?.channel || '-';

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    handleProductSelection(event, produto.id || produto._id);
  };

  const getStockLabel = () => {
    if (stock === 0) return { label: 'Esgotado', color: '#f44336', bgColor: '#ffebee' };
    if (stock < 10) return { label: 'Baixo', color: '#ff9800', bgColor: '#fff3e0' };
    return { label: 'Disponível', color: '#4caf50', bgColor: '#e8f5e9' };
  };

  const stockInfo = getStockLabel();
  return (
    <ListItem
      alignItems="center"
      sx={{
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        bgcolor: isSelected ? '#e3f2fd' : '#fff',
        boxShadow: isSelected ? '0 0 0 2px #90caf9' : '0 2px 8px rgba(0,0,0,0.03)',
        transition: 'box-shadow 0.2s, background 0.2s',
        position: 'relative',
        opacity: isActive ? 1 : 0.7,
        py: 1,
        px: 1,
        pr: { xs: 1, sm: 7 }, // padding extra à direita para o botão de ações
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        minHeight: 120
      }}
      secondaryAction={
        <ListItemSecondaryAction>
          <IconButton onClick={handleMenuClick} size="small">
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            elevation={2}
            PaperProps={{ sx: { minWidth: 160, borderRadius: 2 } }}
          >
            <MenuItem onClick={() => { handleStartEditPrice(produto); handleMenuClose(); }}>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Editar Preço
            </MenuItem>
            <MenuItem onClick={() => { handleToggleProductActive(produto); handleMenuClose(); }}>
              <ListItemIcon>
                {isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
              </ListItemIcon>
              {isActive ? 'Desativar' : 'Ativar'}
            </MenuItem>
          </Menu>
        </ListItemSecondaryAction>
      } disablePadding
    >
      {/* Checkbox de seleção */}
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', ml: { xs: 0, sm: 0.5 }, mr: { xs: 0, sm: 1 } }}>
        <Checkbox
          checked={isSelected}
          onChange={handleCheckboxClick}
          sx={{
            alignSelf: 'center',
            color: '#1976d2',
            p: 0.5
          }}
        />
      </Box>
      {/* Avatar/Imagem do produto */}
      <ListItemAvatar sx={{
        alignSelf: { xs: 'center', sm: 'center' },
        display: 'flex',
        alignItems: 'center',
        minWidth: 80,
        mr: { xs: 0, sm: 2 },
        mb: { xs: 1, sm: 0 }
      }}>
        <Avatar
          variant="rounded"
          src={hasImage && typeof hasImage === 'string' ? hasImage : (produto.images && produto.images.length > 0 ? produto.images[0]?.url || produto.images[0]?.secure_url : undefined) || 'https://via.placeholder.com/80x80?text=Sem+Imagem'}
          alt={produto.title}
          sx={{
            width: 80,
            height: 80,
            bgcolor: '#f5f5f5',
            opacity: isActive ? 1 : 0.6,
            border: '1px solid #e0e0e0',
            objectFit: 'cover'
          }}
          imgProps={{ style: { objectFit: 'cover', width: '100%', height: '100%' } }}
        />
      </ListItemAvatar>
      {/* Conteúdo principal + preços */}
      <Box sx={{
        display: 'flex',
        flex: 1,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        width: '100%',
        minWidth: 0,
        gap: { xs: 1, sm: 2 }
      }}>
        {/* Conteúdo central */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} color={isActive ? '#222' : 'text.disabled'} noWrap sx={{ maxWidth: { xs: '100%', sm: 420 }, lineHeight: 1.3 }}>
              {produto.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              <Chip icon={<StorefrontOutlinedIcon sx={{ fontSize: 16, color: '#ff8f00' }} />} label={canal} size="small" sx={{ bgcolor: '#fff8e1', color: '#ff8f00', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip icon={<LocalOfferOutlinedIcon sx={{ fontSize: 16, color: '#1976d2' }} />} label={`SKU: ${meliSku}`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }} />
              {!isActive && (
                <Chip icon={<CancelOutlinedIcon sx={{ fontSize: 16, color: 'white' }} />} label="Inativo" size="small" sx={{ ml: 1, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '0.75rem' }} />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, alignItems: { md: 'flex-start' }, flexWrap: 'wrap', mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2OutlinedIcon sx={{ fontSize: 18, color: stockInfo.color }} />
              <Chip
                label={stockInfo.label}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500, fontSize: '0.7rem', color: stockInfo.color, bgcolor: stockInfo.bgColor, border: 'none' }}
              />
              <Typography variant="body2" color="text.secondary">
                {stock} unid. / Meli: {meliAvailable}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                ID: {produto?.skus?.[0]?.partnerId || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status Meli: <b style={{ color: meliStatus === 'active' ? '#388e3c' : '#f44336', display: 'inline-flex', alignItems: 'center' }}>{meliStatus === 'active' ? <CheckCircleOutlineIcon sx={{ fontSize: 16, ml: 0.5, color: '#388e3c' }} /> : <CancelOutlinedIcon sx={{ fontSize: 16, ml: 0.5, color: '#f44336' }} />} {meliStatus}</b>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Criado em: {meliDateCreated}
              </Typography>
              {meliPermalink && (
                <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                  <a href={meliPermalink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#1976d2' }}>Ver anúncio</a>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        {/* Coluna de preços */}
        <Box sx={{
          minWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          justifyContent: 'flex-start',
          gap: 0.5,
          mt: { xs: 1, sm: 0 },
          mr: { xs: 0, sm: 4 } // Espaço extra à direita para não sobrepor o botão de ações
        }}>
          {isEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl variant="outlined" size="small">
                <OutlinedInput
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                  autoFocus
                  sx={{ width: 120, height: 32 }}
                />
              </FormControl>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Salvar">
                  <IconButton size="small" onClick={handleSavePrice} sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' } }}>
                    <SaveIcon fontSize="small" sx={{ color: '#4caf50' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancelar">
                  <IconButton size="small" onClick={handleCancelEditPrice} sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' } }}>
                    <CloseIcon fontSize="small" sx={{ color: '#f44336' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5 }}>
              <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#e8f5e9', borderRadius: 1, mb: 0.2, minWidth: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#388e3c', fontSize: 15, mr: 1 }}>
                  CMV:
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#388e3c', fontSize: 18 }}>
                  {formatPrice(precoMercado)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#1976d2', fontSize: 14, mt: 0.5 }}>
                Anúncio: {precoVitrine ? formatPrice(precoVitrine) : '-'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

export default CatalogProductListItem;
